import {
  chunk,
  deduplicateById,
  displayCodeableConcepts,
  isSupportedActorResource,
  mapActorResource,
  parseReference,
  referenceKey,
} from "./fhir.utils";
import {
  ActorModel,
  FhirBundle,
  FhirClientPort,
  ScheduleResource,
  SupportedActorType,
} from "./fhir.types";

export interface ScheduleModel {
  id: string;
  active: boolean;
  actor: ActorModel;
  serviceLabels: string[];
  comment?: string;
}

function actorMapFromBundle(bundle: FhirBundle): Map<string, ActorModel> {
  const result = new Map<string, ActorModel>();

  (bundle.entry ?? []).forEach((entry) => {
    if (isSupportedActorResource(entry.resource)) {
      const actor = mapActorResource(entry.resource);
      result.set(actor.reference, actor);
    }
  });

  return result;
}

function fallbackActor(
  actorReference: string,
  display: string | undefined,
): ActorModel | undefined {
  const parsed = parseReference(actorReference);
  if (!parsed) {
    return undefined;
  }

  const type =
    parsed.resourceType === "Patient"
      ? "patient"
      : parsed.resourceType === "Practitioner"
        ? "practitioner"
        : parsed.resourceType === "Location"
          ? "location"
          : parsed.resourceType === "Device"
            ? "device"
            : undefined;

  if (!type) {
    return undefined;
  }

  return {
    id: parsed.id,
    reference: parsed.key,
    type,
    display: display?.trim() || parsed.key,
    active: true,
  };
}

function mapSchedule(
  resource: ScheduleResource,
  actorMap: Map<string, ActorModel>,
): ScheduleModel | undefined {
  if (!resource.id) {
    return undefined;
  }

  const actorReference = resource.actor?.[0]?.reference;
  const key = referenceKey(actorReference);
  const actor =
    (key ? actorMap.get(key) : undefined) ??
    (actorReference
      ? fallbackActor(actorReference, resource.actor?.[0]?.display)
      : undefined);

  if (!actor) {
    return undefined;
  }

  return {
    id: resource.id,
    active: resource.active !== false,
    actor,
    serviceLabels: Array.from(
      new Set([
        ...displayCodeableConcepts(resource.serviceType),
        ...displayCodeableConcepts(resource.specialty),
        ...displayCodeableConcepts(resource.serviceCategory),
      ]),
    ),
    comment: resource.comment?.trim() || undefined,
  };
}

export class ScheduleService {
  constructor(private readonly client: FhirClientPort) {}

  async searchByActors(actors: ActorModel[]): Promise<ScheduleModel[]> {
    if (actors.length === 0) {
      return [];
    }

    const knownActors = new Map(actors.map((actor) => [actor.reference, actor]));
    const responses = await Promise.all(
      chunk(actors, 20).map((actorChunk) =>
        this.client.search("Schedule", {
          active: true,
          actor: actorChunk.map((actor) => actor.reference).join(","),
          _include: "Schedule:actor",
          _count: 100,
        }),
      ),
    );

    const schedules = responses.flatMap((bundle) => {
      const actorsFromBundle = actorMapFromBundle(bundle);
      knownActors.forEach((actor, reference) => {
        if (!actorsFromBundle.has(reference)) {
          actorsFromBundle.set(reference, actor);
        }
      });

      return (bundle.entry ?? [])
        .map((entry) => entry.resource)
        .filter(
          (resource): resource is ScheduleResource =>
            resource?.resourceType === "Schedule",
        )
        .map((resource) => mapSchedule(resource, actorsFromBundle))
        .filter((schedule): schedule is ScheduleModel => Boolean(schedule));
    });

    return deduplicateById(schedules);
  }

  async searchDirectory(
    actorType: SupportedActorType,
    query: string,
  ): Promise<ScheduleModel[]> {
    const chainByActorType: Record<SupportedActorType, string> = {
      patient: "actor:Patient.name",
      practitioner: "actor:Practitioner.name",
      location: "actor:Location.name",
      device: "actor:Device.device-name",
    };
    const normalized = query.trim();
    const bundle = await this.client.search("Schedule", {
      active: true,
      ...(normalized ? { [chainByActorType[actorType]]: normalized } : {}),
      _include: "Schedule:actor",
      _count: 100,
    });
    const actorMap = actorMapFromBundle(bundle);

    return deduplicateById(
      (bundle.entry ?? [])
        .map((entry) => entry.resource)
        .filter(
          (resource): resource is ScheduleResource =>
            resource?.resourceType === "Schedule",
        )
        .map((resource) => mapSchedule(resource, actorMap))
        .filter(
          (schedule): schedule is ScheduleModel =>
            Boolean(schedule && schedule.actor.type === actorType),
        ),
    );
  }

  async getById(id: string): Promise<ScheduleModel | undefined> {
    const bundle = await this.client.search("Schedule", {
      _id: id,
      _include: "Schedule:actor",
      _count: 1,
    });
    const actorMap = actorMapFromBundle(bundle);
    const schedule = (bundle.entry ?? [])
      .map((entry) => entry.resource)
      .find(
        (resource): resource is ScheduleResource =>
          resource?.resourceType === "Schedule" && resource.id === id,
      );

    return schedule ? mapSchedule(schedule, actorMap) : undefined;
  }
}
